import type { FC } from "react";

import Stepper from "@/components/shared/Stepper/Stepper.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import Destination, {
  type DestinationSchema,
} from "@/components/functional/TripManipulationForm/steps/Destination.tsx";
import DateStep, {
  type DateSchema,
} from "@/components/functional/TripManipulationForm/steps/DateStep.tsx";
import WithWho, {
  type WithWhoSchema,
} from "@/components/functional/TripManipulationForm/steps/WithWho.tsx";
import WhatHasBeenBooked, {
  type whatHasBeenBookedSchema,
} from "@/components/functional/TripManipulationForm/steps/WhatHasBeenBooked.tsx";
import MoreInfo, {
  type MoreInfoSchema,
} from "@/components/functional/TripManipulationForm/steps/MoreInfo.tsx";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import type { TripData } from "$/models/Trip.model.ts";

export type ActiveTripCreateStep = (typeof allTripCreateSteps)[number];

export type OnCompleteArgs = { context: TripData } & (
  | { step: typeof TripCreateStep.DESTINATION; data: DestinationSchema }
  | { step: typeof TripCreateStep.DATE; data: DateSchema }
  | { step: typeof TripCreateStep.WITH_WHO; data: WithWhoSchema }
  | {
      step: typeof TripCreateStep.WHAT_HAS_BEEN_BOOKED;
      data: whatHasBeenBookedSchema;
    }
  | { step: typeof TripCreateStep.MORE_INFO; data: MoreInfoSchema }
);

type TripManipulationFormProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
  initialData?: TripData;
  initialStep?: ActiveTripCreateStep;
  singleStep?: boolean;
  isLoadingStep?: boolean;
};

const TripManipulationForm: FC<TripManipulationFormProps> = ({
  onComplete,
  onCompleteWhenDirty,
  initialData,
  initialStep,
  singleStep = false,
  isLoadingStep,
}) => {
  const { user } = useAuthContext();
  const currentStep = initialStep ?? allTripCreateSteps[0];

  if (!currentStep) {
    console.error(`Invalid trip create step for user (${user?.id})`);
    return null;
  }

  const stepperSteps = singleStep
    ? ([currentStep] as const)
    : allTripCreateSteps;

  const stepProps = {
    onComplete,
    onCompleteWhenDirty,
  };

  return (
    <Stepper.Wrapper
      isLoadingStep={isLoadingStep}
      steps={stepperSteps}
      initialContextData={initialData}
      initialStep={currentStep}
    >
      <Stepper.Step<typeof allTripCreateSteps> step={TripCreateStep.DESTINATION}>
        <Destination {...stepProps} />
      </Stepper.Step>

      <Stepper.Step<typeof allTripCreateSteps> step={TripCreateStep.DATE}>
        <DateStep {...stepProps} />
      </Stepper.Step>

      <Stepper.Step<typeof allTripCreateSteps> step={TripCreateStep.WITH_WHO}>
        <WithWho {...stepProps} />
      </Stepper.Step>

      <Stepper.Step<typeof allTripCreateSteps>
        step={TripCreateStep.WHAT_HAS_BEEN_BOOKED}
      >
        <WhatHasBeenBooked {...stepProps} />
      </Stepper.Step>

      <Stepper.Step<typeof allTripCreateSteps> step={TripCreateStep.MORE_INFO}>
        <MoreInfo {...stepProps} />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default TripManipulationForm;